import type { OnboardingItem, TenantOnboarding } from '../../api/types';
import { Badge, Stat, formatDate, type Tone } from '../ui';

/** done (activated), detected (the hub can see it but nobody switched it on), or pending. */
export function itemStatus(item: OnboardingItem): { label: string; tone: Tone } {
  if (item.activated) return { label: 'done', tone: 'ok' };
  if (item.detected) return { label: 'detected', tone: 'accent' };
  return { label: 'pending', tone: 'neutral' };
}

/**
 * Everything the platform plan includes, grouped by category, and whether
 * each item is switched on. Read-only in the portal; on the console each
 * row has a checkbox and a notes input (saved on blur). `pending` names the
 * item whose change is in flight, so its controls sit disabled meanwhile.
 */
export function OnboardingChecklist({
  data,
  editable,
  onToggle,
  onNotes,
  pending = null,
}: {
  data: TenantOnboarding;
  editable: boolean;
  onToggle?: (key: string, activated: boolean) => void;
  onNotes?: (key: string, notes: string) => void;
  pending?: string | null;
}) {
  const { progress } = data;
  return (
    <div className="stack">
      <div className="stats">
        <Stat label="Activated" value={progress.activated} hint={`of ${progress.total}`} />
        <Stat label="Detected" value={progress.detected} hint="seen by the hub" />
        <Stat
          label="Remaining"
          value={progress.total - progress.activated}
          hint="still to activate"
        />
      </div>
      {data.categories.map((category) => (
        <section key={category.key} className="checklist" aria-label={category.name}>
          <p className="checklist__heading">{category.name}</p>
          <div className="table-wrap">
            <table className="table table--nested">
              <thead>
                <tr>
                  {editable ? <th aria-label="Activated" /> : null}
                  <th>Item</th>
                  <th>Status</th>
                  <th>Detected</th>
                  {editable ? <th>Notes</th> : null}
                </tr>
              </thead>
              <tbody>
                {category.items.map((item) => (
                  <ChecklistRow
                    key={item.key}
                    item={item}
                    editable={editable}
                    busy={pending === item.key}
                    onToggle={onToggle}
                    onNotes={onNotes}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function ChecklistRow({
  item,
  editable,
  busy,
  onToggle,
  onNotes,
}: {
  item: OnboardingItem;
  editable: boolean;
  busy: boolean;
  onToggle?: (key: string, activated: boolean) => void;
  onNotes?: (key: string, notes: string) => void;
}) {
  const status = itemStatus(item);
  return (
    <tr>
      {editable ? (
        <td className="checklist__check">
          <input
            type="checkbox"
            checked={item.activated}
            disabled={busy}
            aria-label={`${item.label} activated`}
            onChange={(e) => onToggle?.(item.key, e.target.checked)}
          />
        </td>
      ) : null}
      <td>
        {item.label}
        {typeof item.value === 'string' ? (
          <>
            {' '}
            <code>{item.value}</code>
          </>
        ) : null}
        {editable && item.activated && item.activatedBy ? (
          <div className="checklist__sub">
            activated by {item.activatedBy.username} on {formatDate(item.activatedAt)}
          </div>
        ) : null}
        {!editable && item.notes ? <div className="checklist__sub">{item.notes}</div> : null}
      </td>
      <td>
        <Badge tone={status.tone}>{status.label}</Badge>
      </td>
      <td className="muted checklist__detail">{item.detectedDetail ?? '—'}</td>
      {editable ? (
        <td>
          <input
            // Remounts when the saved note changes, so the box always starts from what is stored.
            key={item.notes ?? ''}
            type="text"
            className="field__input checklist__notes"
            defaultValue={item.notes ?? ''}
            placeholder="Notes"
            aria-label={`Notes for ${item.label}`}
            disabled={busy}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (item.notes ?? '')) onNotes?.(item.key, next);
            }}
          />
        </td>
      ) : null}
    </tr>
  );
}
